import { SimpleFilter, SoundTouch } from 'soundtouchjs'
import type { AudioCtx } from './play/soundchunked'

class WebAudioBufferSource {
  buffer: AudioBuffer
  _position: number

  constructor(buffer: AudioBuffer) {
    this.buffer = buffer
    this._position = 0
  }
  get dualChannel() {
    return this.buffer.numberOfChannels > 1
  }
  get position() {
    return this._position
  }
  set position(value) {
    this._position = value
  }
  extract(target, numFrames = 0, position = 0) {
    this.position = position
    const left = this.buffer.getChannelData(0)
    for (let i = 0; i < numFrames; i++) target[i * 2] = target[i * 2 + 1] = left[i + position]
    return Math.min(numFrames, left.length - position)
  }
}

export async function changePlayback(buf: Float32Array, sampleRate = 16000, speed = 1) {
  const padding = 8192
  const audioBuffer = new AudioBuffer({ length: buf.length + padding, numberOfChannels: 1, sampleRate })
  audioBuffer.copyToChannel(buf, 0)

  const soundTouch = new SoundTouch()
  soundTouch.tempo = speed
  const filter = new SimpleFilter(new WebAudioBufferSource(audioBuffer), soundTouch)
  const estBufSize = Math.floor(buf.length / speed) * 3
  const processedBuffer = new Float32Array(estBufSize)
  const rawLength = filter.extract(processedBuffer, estBufSize)
  const length = Math.min(Math.max(rawLength - (padding * 2) / speed, Math.floor(buf.length / speed)), rawLength)

  const result = new Float32Array(length)
  const stride = 2
  const strideView = new Float32Array(processedBuffer.buffer, 0, Math.min(length * stride, processedBuffer.length))

  for (let i = 0; i < length; i += 4) {
    result.set(
      strideView.subarray(i * stride, (i + 4) * stride).filter((_, index) => index % 2 === 0),
      i
    )
  }

  const remainingStart = Math.floor(length / 4) * 4
  for (let i = remainingStart; i < length; i++) result[i] = processedBuffer[i * stride]

  return result
}

// Parse audio chunk and decode with speed
export async function decodeChunk(audioCtx: AudioCtx, chunk: Uint8Array, speed = 1, replay = false) {
  try {
    let audioBuffer: AudioBuffer
    let intBuffer: Int16Array | null = null

    if (!replay) {
      audioBuffer = await audioCtx.decodeAudioData(chunk.buffer)
      if (audioBuffer.length < 160) return [null, null]

      const delay = Math.floor(0.25 * audioBuffer.sampleRate)
      const newBuffer = new Float32Array(audioBuffer.length + delay)
      newBuffer.set(audioBuffer.getChannelData(0))

      intBuffer = new Int16Array(newBuffer.length)
      for (let i = 0; i < newBuffer.length; i++) {
        intBuffer[i] = Math.floor(newBuffer[i] * 32767)
      }

      audioBuffer = audioCtx.createBuffer(1, newBuffer.length, audioBuffer.sampleRate)
      audioBuffer.copyToChannel(newBuffer, 0)
    } else {
      audioBuffer = await audioCtx.decodeAudioData(chunk.buffer)
    }

    const changedBuf = await changePlayback(audioBuffer.getChannelData(0), audioBuffer.sampleRate, speed)
    const result = audioCtx.createBuffer(1, changedBuf.length, audioBuffer.sampleRate)
    result.copyToChannel(changedBuf, 0)

    return [result, intBuffer] as [AudioBuffer, Int16Array]
  } catch (e) {
    console.error(e)
    return [null, null]
  }
}
